import type { GeneratedIntelligenceAnswer } from './types';
import { isIOSPath } from '../../lib/web-presentation';

type ContextKind = 'CLIENT' | 'PORTFOLIO';

type AssistantActivity = NonNullable<GeneratedIntelligenceAnswer['activities']>[number];
type AssistantCitation = GeneratedIntelligenceAnswer['citations'][number];

export const askAmoyeDataUseHref = '/privacy/ask-amoye-data-use.html';

export function presentAssistantMethod(modelId?: string, _modelVersion?: string): {
  label: string; value: string;
} {
  const id = modelId?.trim() ?? '';
  if (!id) return { label: 'Answer method', value: 'Method not recorded for this earlier answer' };
  if (/^(?:amoye-(?:deterministic|local-grounded|mock)|mock)/i.test(id)) {
    return { label: 'Answer method', value: 'Amoye rules-based guidance — no language model used' };
  }
  if (/llama/i.test(id)) return { label: 'Language model', value: humanModelName(id, 'Meta Llama') };
  if (/qwen/i.test(id)) return { label: 'Language model', value: humanModelName(id, 'Qwen') };
  if (/deepseek/i.test(id)) return { label: 'Language model', value: humanModelName(id, 'DeepSeek') };
  if (/mistral|mixtral/i.test(id)) return { label: 'Language model', value: humanModelName(id, 'Mistral') };
  return { label: 'Language model', value: 'Amoye’s configured tax-guidance model' };
}

function humanModelName(modelId: string, family: string): string {
  const leaf = modelId.split('/').at(-1) ?? modelId;
  const withoutFamily = leaf.replace(new RegExp(family.replace('Meta ', '').replace(' ', '[-_ ]?'), 'ig'), '')
    .replace(/^meta[-_ ]?/i, '').replace(/^[-_ ]+|[-_ ]+$/g, '');
  const detail = withoutFamily.replace(/[-_]+/g, ' ').replace(/\b(\d+)b\b/ig, '$1B')
    .replace(/\b(v\d+|q\d+|r\d+)\b/ig, (value) => value.toUpperCase())
    .replace(/\b\w/g, (value) => value.toUpperCase()).trim();
  return detail ? `${family} ${detail}` : family;
}

function countIn(label: string): number | undefined {
  const match = label.match(/\b(\d+)\b/);
  return match ? Number(match[1]) : undefined;
}

/** Converts stable assistant activity codes to plain, user-facing progress.
 * The API label remains available to audit records and is never rewritten. */
export function presentAssistantActivity(activity: AssistantActivity, contextKind: ContextKind): string {
  const count = countIn(activity.label);
  switch (activity.code) {
    case 'CONSENT_CONFIRMED':
      return contextKind === 'PORTFOLIO'
        ? 'Confirmed I can use the practice summary available to you'
        : 'Confirmed I can use records from this tax profile';
    case 'INTENTS_PLANNED':
      return count && count > 1 ? `Understood the ${count} parts of your question` : 'Understood your question';
    case 'AUTHORIZED_RECORDS_READ':
      if (contextKind === 'PORTFOLIO') {
        return count === undefined ? 'Checked the permitted practice summary'
          : `Checked ${count} assigned client ${count === 1 ? 'record' : 'records'}`;
      }
      return count === undefined ? 'Checked the relevant records'
        : `Checked ${count} relevant ${count === 1 ? 'record' : 'records'}`;
    case 'RESPONSE_VALIDATED':
      return 'Checked the answer and any links before showing them';
    default:
      return presentLegacyActivityLabel(activity.label, contextKind);
  }
}

function presentLegacyActivityLabel(label: string, contextKind: ContextKind): string {
  if (/consent attached|consent confirmed/i.test(label)) {
    return contextKind === 'PORTFOLIO'
      ? 'Confirmed I can use the practice summary available to you'
      : 'Confirmed I can use records from this tax profile';
  }
  if (/request intents? planned/i.test(label)) {
    const count = countIn(label);
    return count && count > 1 ? `Understood the ${count} parts of your question` : 'Understood your question';
  }
  if (/authori[sz]ed read|permitted records read/i.test(label)) {
    const count = countIn(label);
    return count === undefined ? 'Checked the relevant records'
      : `Checked ${count} relevant ${count === 1 ? 'record' : 'records'}`;
  }
  if (/response .*validated|navigation validated/i.test(label)) {
    return 'Checked the answer and any links before showing them';
  }
  if (/source references? returned/i.test(label)) {
    const count = countIn(label) ?? 0;
    return count > 0 ? `Included ${count} ${count === 1 ? 'source' : 'sources'} you can check`
      : 'No source was available for this answer';
  }
  if (/request stopped when intelligence was turned off/i.test(label)) return 'Stopped because Ask Amoye was turned off';
  if (/authori[sz]ed request did not complete/i.test(label)) return 'Could not finish checking the records';
  if (/saved conversation could not be read/i.test(label)) return 'Could not load the saved conversation';
  return 'Completed an answer check';
}

/** Keeps the governed answer intact while hiding machine citation markers and
 * translating a small number of legacy deterministic phrases. */
export function presentAssistantAnswer(answer: string): string {
  const value = decodeVisibleEntities(answer)
    .replace(/\s*\[citation:[^\]\s]+\]/g, '')
    .replace(/I read the current (personal|business|client) workflow for [^,.;]+,\s*([^.;]+)\.\s*First:/i,
      (_match, surface: string, period: string) => `I checked the current ${surface.toLowerCase()} tax work for ${period.trim()}. Start here:`)
    .replace(/No evidence is registered for the selected obligation and period\.?/gi,
      'No documents are linked to this tax return for the selected period.')
    .replace(/\b(?:the\s+)?(?:supplied\s+)?authori[sz]ed (?:read |tool )?results?\b/gi, 'the records I checked')
    .replace(/\bevidence bundles?\b/gi, 'supporting information')
    .replace(/\btax[- ]law (?:extracts?|passages?)\b/gi, 'tax guidance')
    .replace(/\bdeterministic (?:candidate )?(?:tax )?estimate\b/gi, 'current tax estimate')
    .replace(/\bdeterministic (?:calculation )?(?:result|trace)\b/gi, 'calculation details')
    .replace(/\bcited (?:calculation )?trace\b/gi, 'calculation details')
    .replace(/\bintent planners?\b/gi, 'question handling')
    .replace(/\brequest intents?\b/gi, 'parts of your question')
    .replace(/\b(?:the\s+)?RAG retrieval context\b/gi, 'the information I checked')
    .replace(/\bretrieval context\b/gi, 'records')
    .replace(/\bRAG\b/g, 'the records search')
    .replace(/\bmodel gateway\b/gi, 'answer service')
    .replace(/\bpersonal workflow\b/gi, 'personal tax work')
    .replace(/\bbusiness workflow\b/gi, 'business tax work')
    .replace(/\bclient workflow\b/gi, 'client tax work')
    .replace(/\bAMOYE-[A-Z0-9-]{3,}\b/g, 'this tax return')
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi, 'this record')
    .replace(/[ \t]+/g, ' ')
    .replace(/(^|[.!?]\s+)([a-z])/g, (_match, prefix: string, letter: string) => `${prefix}${letter.toUpperCase()}`)
    .trim();
  return /^(?:I cannot|I can['’]t|I could not|I am not sure|No\b|There (?:is|are) no\b)/i.test(value)
    ? `What I can tell you:\n${value}` : value;
}

export type AssistantAnswerBlock =
  | { kind: 'heading'; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; ordered: boolean; items: string[] };

/** Turns the model's deliberately small plain-text/Markdown subset into safe,
 * semantic UI blocks. No HTML is accepted or injected. */
export function presentAssistantAnswerBlocks(answer: string): AssistantAnswerBlock[] {
  const lines = presentAssistantAnswer(answer).replaceAll('\r\n', '\n').split('\n');
  const blocks: AssistantAnswerBlock[] = [];
  let paragraph: string[] = [];
  let list: Extract<AssistantAnswerBlock, { kind: 'list' }> | undefined;
  const flushParagraph = () => {
    const text = cleanInlineMarkup(paragraph.join(' ').trim());
    if (text) blocks.push({ kind: 'paragraph', text });
    paragraph = [];
  };
  const endList = () => { list = undefined; };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) { flushParagraph(); endList(); continue; }
    const heading = headingText(line);
    if (heading) {
      flushParagraph(); endList(); blocks.push({ kind: 'heading', text: heading }); continue;
    }
    const listItem = line.match(/^(?:([-*•])|(\d+)[.)])\s+(.+)$/);
    if (listItem) {
      flushParagraph();
      const ordered = Boolean(listItem[2]);
      if (!list || list.ordered !== ordered) {
        list = { kind: 'list', ordered, items: [] };
        blocks.push(list);
      }
      list.items.push(cleanInlineMarkup(listItem[3]!.trim()));
      continue;
    }
    endList(); paragraph.push(line);
  }
  flushParagraph();
  return blocks;
}

function cleanInlineMarkup(value: string): string {
  return value.replace(/\*\*([^*\n]+)\*\*/g, '$1').replace(/__([^_\n]+)__/g, '$1')
    .replace(/`([^`\n]+)`/g, '$1');
}

function headingText(value: string): string | undefined {
  const markdown = value.match(/^#{1,6}\s+(.{1,100})$/)?.[1]
    ?? value.match(/^\*\*([^*\n]{1,100})\*\*:?$/)?.[1];
  if (markdown) return markdown.replace(/:$/, '').trim();
  if (value.length <= 100 && /^[\p{L}][\p{L}\p{M}\s/&-]+:$/u.test(value)) return value.slice(0, -1).trim();
  return undefined;
}

function decodeVisibleEntities(value: string): string {
  const named: Record<string, string> = { amp: '&', apos: "'", gt: '>', lt: '<', nbsp: ' ', quot: '"' };
  return value
    .replace(/&#x([0-9a-f]{1,6});/gi, (match, digits: string) => codePoint(digits, 16) ?? match)
    .replace(/&#([0-9]{1,7});/g, (match, digits: string) => codePoint(digits, 10) ?? match)
    .replace(/&(amp|apos|gt|lt|nbsp|quot);/gi, (match, name: string) => named[name.toLowerCase()] ?? match);
}

function codePoint(digits: string, radix: number): string | undefined {
  const value = Number.parseInt(digits, radix);
  if (!Number.isSafeInteger(value) || value < 0x09 || value > 0x10ffff || (value >= 0xd800 && value <= 0xdfff)) {
    return undefined;
  }
  return String.fromCodePoint(value);
}

export function presentAssistantLimitation(limitation: string): string {
  if (/tax-law passages are unreviewed test content/i.test(limitation)) {
    return 'This answer uses tax-law material that is still being tested and has not been approved by a tax practitioner. Do not rely on it to file a return or give professional advice.';
  }
  if (/cannot accept facts, calculate tax, approve, sign, submit(?:, file)?, issue an invoice or pay/i.test(limitation)
    || /cannot accept facts, calculate tax, approve, sign, submit or pay/i.test(limitation)) {
    return isIOSPath()
      ? 'I can explain what the records show and point you to the right screen. I cannot accept evidence, calculate or change tax, approve, sign, file, issue an invoice, or make a payment for you.'
      : 'I can explain what the records show and point you to the right screen. I cannot accept records, calculate or change tax, approve, sign, file, issue an invoice, or make a payment for you.';
  }
  if (/read-only assistance/i.test(limitation) || /^read only\.?$/i.test(limitation.trim())) {
    return 'Check the cited records and any calculation details before relying on this answer. I can explain what those records show, but I cannot change them or complete a tax action for you.';
  }
  const partial = limitation.match(/(\d+) parts? of the request could not be handled/i);
  if (partial) return `I answered the parts supported by the available information. ${partial[1]} ${partial[1] === '1' ? 'part needs' : 'parts need'} a more specific question or additional detail.`;
  if (/not executed as an inferred action/i.test(limitation)) {
    return 'I can guide you after you name the action you want. Try asking what to do next, what is blocking the work, or about your documents, calculation, return, deadline, or invoice.';
  }
  return limitation;
}

/** Combines overlapping machine-policy limitations into one short boundary.
 * Exceptional warnings remain visible, but the user does not see the same
 * read-only disclaimer expressed two or three different ways. */
export function presentAssistantLimitations(limitations: readonly string[]): string[] {
  const result: string[] = [];
  const add = (value: string) => { if (value && !result.includes(value)) result.push(value); };
  for (const limitation of limitations) {
    if (/unreviewed test|test tax guidance|has not (?:yet )?been approved by a tax practitioner/i.test(limitation)) {
      add('This answer uses test tax guidance that has not been approved by a tax practitioner. Do not use it to file a return or give professional advice.');
    } else if (/read[- ]only|cannot accept facts|cannot change records|can(?:not|'t) calculate tax|verify cited facts|calculation trace/i.test(limitation)) {
      add('I can explain and guide you, but I cannot change records, calculate tax, approve, file, or pay for you.');
    } else {
      add(presentAssistantLimitation(limitation));
    }
  }
  return result;
}

export function presentAssistantError(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error ?? '');
  const code = error && typeof error === 'object' && 'code' in error && typeof error.code === 'string'
    ? error.code : undefined;
  if (code === 'consent_required' || /active .*consent required|processing consent required/i.test(message)) {
    return 'Permission is needed before Ask Amoye can use this tax profile. Turn record access on in Amoye settings and try again.';
  }
  if (/conversation history unavailable/i.test(message)) {
    return 'Your saved conversation is still protected. It could not be opened right now, and nothing was deleted. Please try again.';
  }
  if (/request could not be safely planned|question needs clarification/i.test(message)) {
    return 'I can help with your next step, documents, return, deadline, calculation or invoice. Name the item you want checked and the outcome you need.';
  }
  if (/context unavailable|context required|assignment is no longer active|authorization changed/i.test(message)) {
    return 'This tax profile or client assignment has changed. Reopen the correct profile and try again.';
  }
  if (/no intelligence-enabled client assignments/i.test(message)) {
    return 'Here’s what you can do next: open an assigned client task, or ask a practice administrator to add Intelligence access to an active assignment. Ask Amoye can compare priorities once at least one assigned client is available.';
  }
  if (/tax-law material exists .* no relevant passage matched/i.test(message)) {
    return 'Relevant Nigerian tax-law material is available for this tax type and period. A closer match needs one specific tax benefit, rule or document type; ask about that item and try again. Nothing was changed.';
  }
  if (/reviewed Nigerian tax-law extracts are unavailable/i.test(message)) {
    return 'A reliable answer needs approved Nigerian tax-law material for this tax type and period, and that material is not available yet. Nothing was changed.';
  }
  if (/reviewed Nigerian tax-law extracts .* stale/i.test(message)) {
    return 'The Nigerian tax-law material for this tax type and period needs a currentness check before it can support an answer. Nothing was changed.';
  }
  if (code === 'intelligence_answer_not_verified' || /answer .*could not safely verify/i.test(message)
    || /claims or sources did not pass verification/i.test(message)) {
    return 'Your work is unchanged. This draft was withheld because its claims or sources did not pass verification. Narrow the question to one tax issue, period or document so Ask Amoye can check it again.';
  }
  if (/could not reach the configured intelligence model/i.test(message)) {
    return 'Your question is ready to retry when the Ask Amoye answer service is available. Nothing was changed.';
  }
  if (code === 'intelligence_egress_rejected' || /could not safely minimize this request/i.test(message)
    || /could not be prepared within Ask Amoye’s data-use rules/i.test(message)) {
    return 'Your information stayed private because this question could not be prepared within Ask Amoye’s data-use rules. Nothing was sent or changed.';
  }
  if (/^[A-Z][A-Z0-9_]+$/.test(message)) {
    return 'Your work is unchanged and the question is ready to retry. Please try again.';
  }
  return message || 'The available records support a more specific question about status, documents, calculation, deadline or next step. Nothing was changed.';
}

export function presentCitation(citation: AssistantCitation): { title: string; detail: string; href?: string } {
  const titles: Record<string, string> = {
    'amoye-workflow-state': 'Current tax-work status',
    'amoye-filing-support-guide': 'Filing preparation information',
    'local-candidate-tax-content': 'Nigerian tax guidance',
    'practice-authority-snapshot': 'Current practice status',
  };
  const href = citation.sourceUri && /^https:\/\//i.test(citation.sourceUri) ? citation.sourceUri : undefined;
  const proposedTitle = citation.title && !looksInternal(citation.title, citation.sourceId) ? citation.title : undefined;
  const title = proposedTitle ?? titles[citation.sourceId]
    ?? (citation.authority ? `${citation.authority} source` : href ? 'Source information' : 'Workspace information');
  const locator = looksInternal(citation.locator) ? (href ? 'Official publication' : 'Information checked') : citation.locator;
  const detail = citation.authority
    ? `${citation.authority} · ${locator}`
    : href ? locator
      : citation.sourceId === 'amoye-filing-support-guide' ? 'Filing guidance checked'
        : citation.sourceId === 'local-candidate-tax-content' ? 'Tax guidance checked'
          : citation.sourceId === 'practice-authority-snapshot' ? 'Practice summary checked'
            : 'Workspace information checked';
  return { title, detail, ...(href ? { href } : {}) };
}

function looksInternal(value: string, sourceId?: string): boolean {
  const normalized = value.trim();
  return normalized === sourceId
    || /\bAMOYE-[A-Z0-9-]{3,}\b/i.test(normalized)
    || /\b(?:bundle|projection|retrieval|workflow-read|assigned-scopes|context-key)\b/i.test(normalized)
    || /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(normalized)
    || (/^[a-z0-9_-]+(?:\/[a-z0-9_.-]+){2,}$/i.test(normalized) && !/\b(?:section|page)\b/i.test(normalized));
}
